import React from "react";
import { DateField } from "../../molecules/DateField";
import { ScheduleEndsField } from "../../molecules/ScheduleEndsField";
import {
  SchedulePicker,
  type SchedulePickerDropdown,
} from "../../molecules/SchedulePicker";
import type { ScheduleConfig, Weekday } from "./types";
import type { ScheduleErrors } from "../../../utils/scheduleValidation";
import { isoDateTodayLocal } from "../../../utils/scheduleTypes";
import { TIMEZONE_NAMED_OPTIONS } from "../../../utils/scheduleConstants";
import { timezoneOption } from "../../../services/distributions/distributionTime";
import { Field, Label } from "./SendNowForm.styles";
import { HelperText } from "./ScheduleFields.styles";

/**
 * DeliverStory's schedule dropdown namespace — kept distinct from the modal-
 * level `OpenDropdownKey` so SendNowForm can multiplex this alongside its
 * destination/condition dropdowns.
 */
export type ScheduleDropdownKey =
  | "scheduleUnit"
  | "scheduleHour"
  | "scheduleTimezone";

/** "India · Kolkata (IST)": the Distributions page's zone names, with the abbreviation sends are shown in. */
export const DELIVERY_TIMEZONE_OPTIONS = TIMEZONE_NAMED_OPTIONS.map(({ value, label }) => {
  const { abbr } = timezoneOption(value);
  return { value, label: /^[A-Z]{2,5}$/.test(abbr) ? `${label} (${abbr})` : label };
});

/** Map ScheduleFields' enum to the SchedulePicker molecule's enum. */
const toBase = (
  key: ScheduleDropdownKey | null,
): SchedulePickerDropdown | null => {
  switch (key) {
    case "scheduleUnit":
      return "unit";
    case "scheduleHour":
      return "hour";
    case "scheduleTimezone":
      return "timezone";
    default:
      return null;
  }
};

const fromBase = (key: SchedulePickerDropdown): ScheduleDropdownKey => {
  switch (key) {
    case "unit":
      return "scheduleUnit";
    case "hour":
      return "scheduleHour";
    case "timezone":
      return "scheduleTimezone";
  }
};

export interface ScheduleFieldsProps {
  value: ScheduleConfig;
  errors: ScheduleErrors;
  showErrors: boolean;
  openDropdown: ScheduleDropdownKey | null;
  onChange: (patch: Partial<ScheduleConfig>) => void;
  onToggleWeekday: (day: Weekday) => void;
  onOpenDropdown: (key: ScheduleDropdownKey) => void;
  onCloseDropdown: () => void;
}

/**
 * DeliverStory's schedule editor.
 *
 * Composed from the shared `SchedulePicker` molecule (every / repeat-on /
 * refresh-at), the delivery `Starts on` date, and the same
 * `ScheduleEndsField` used by Story refresh.
 */
export const ScheduleFields: React.FC<ScheduleFieldsProps> = ({
  value,
  errors,
  showErrors,
  openDropdown,
  onChange,
  onToggleWeekday,
  onOpenDropdown,
  onCloseDropdown,
}) => {
  const showStartsOnError = showErrors && Boolean(errors.startsOn);

  return (
    <>
      <SchedulePicker
        value={value}
        errors={errors}
        showErrors={showErrors}
        openDropdown={toBase(openDropdown)}
        onChange={onChange}
        onToggleWeekday={onToggleWeekday}
        onOpenDropdown={(key) => onOpenDropdown(fromBase(key))}
        onCloseDropdown={onCloseDropdown}
        timezoneOptions={DELIVERY_TIMEZONE_OPTIONS}
      />

      <Field>
        <Label>Starts on</Label>
        <DateField
          value={value.startsOn}
          min={isoDateTodayLocal()}
          invalid={showStartsOnError}
          ariaLabel="Start date"
          onChange={(iso) => onChange({ startsOn: iso })}
        />
        {showStartsOnError && (
          <HelperText $error>{errors.startsOn}</HelperText>
        )}
      </Field>

      <ScheduleEndsField
        mode={value.endsMode}
        endsOn={value.endsOn}
        endsAfter={value.endsAfter}
        minDate={value.startsOn || undefined}
        onChangeMode={(endsMode) => onChange({ endsMode })}
        onChangeEndsOn={(endsOn) => onChange({ endsOn })}
        onChangeEndsAfter={(endsAfter) => onChange({ endsAfter })}
        endsOnError={errors.endsOn}
        endsAfterError={errors.endsAfter}
        showError={showErrors}
        showAfter={false}
      />
    </>
  );
};

export default ScheduleFields;
